import { useEffect, useState } from 'react';
import { CalendarCheck, ClipboardList, Dumbbell, Flame, Scale, Target, TrendingUp, UserRound } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

/* Kundeninfo neben dem Coach-Chat (Coach-Cockpit W2.7, 29.10.2026): Gewicht, letzter Check-in, Plan, Kalorienziel,
   PT-Kontingent, Phase/Woche und Schnelllinks zu Akte, Fortschritt, Zielen (Übersicht der Akte) und Check-ins. Alles kommt vorgerechnet aus
   GET /api/coach/nachrichten/{id}/kundeninfo (Trainer-Sitzung) — Werte, die der Trainer nicht sehen darf, fehlen dort. */

export const STATUS_TEXT = {
  auf_kurs: 'Auf Kurs', stabil: 'Stabil', beobachten: 'Beobachten', kritisch: 'Kritisch', daten_fehlen: 'Daten fehlen',
};
export const STATUS_FARBE = {
  auf_kurs: 'bg-emerald-500/15 text-emerald-700 dark:bg-[#13301f] dark:text-[#7fd1a0]',
  stabil: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  beobachten: 'bg-amber-500/15 text-amber-700 dark:bg-[#33290f] dark:text-[#f3c46b]',
  kritisch: 'bg-red-500/15 text-red-700 dark:bg-[#3a1c18] dark:text-[#ff9a8a]',
  daten_fehlen: 'bg-muted text-muted-foreground',
};

export function StatusBadge({ status }) {
  const s = status || 'daten_fehlen';
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_FARBE[s] || STATUS_FARBE.daten_fehlen}`} data-testid="coach-status">{STATUS_TEXT[s] || s}</span>;
}

export const tagDe = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');
const CHECKIN = { eingereicht: 'eingereicht', ausgewertet: 'ausgewertet', offen: 'offen', ueberfaellig: 'überfällig', urlaub: 'im Urlaub' };

function Zeile({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-muted-foreground">{label}</p>
        <div className="break-words text-sm font-bold">{children}</div>
      </div>
    </div>
  );
}

export default function CoachKundeninfo({ coach, leadId, gehe, ohneChatLink = false }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  useEffect(() => {
    if (!leadId) return undefined;
    let weg = false;
    setD(null); setFehler('');
    coach.get(`/nachrichten/${leadId}/kundeninfo`)
      .then((r) => { if (!weg) setD(r.data); })
      .catch((e) => { if (!weg) setFehler(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Kundeninfo nicht geladen'); });
    return () => { weg = true; };
  }, [coach, leadId]);

  if (fehler) return <section className="p1-karte border bg-card p-5 text-sm text-muted-foreground" data-testid="coach-kundeninfo">{fehler}</section>;
  if (!d) return <Skeleton className="h-64 rounded-[14px]" />;
  const g = d.gewicht;
  const knopf = 'min-h-[44px] rounded-[10px] border border-border bg-card px-3 text-sm font-bold transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <section className="p1-karte border bg-card p-5" data-testid="coach-kundeninfo" aria-label="Kundeninfo">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">Kundeninfo</p>
          <h3 className="break-words text-base font-extrabold">{d.name}</h3>
        </div>
        <StatusBadge status={d.status} />
      </div>
      <div className="divide-y divide-border/60">
        {g && (
          <Zeile icon={Scale} label="Gewicht">
            {String(g.kg).replace('.', ',')} kg <span className="font-medium text-muted-foreground">· {tagDe(g.am)}</span>
            {g.delta_4w != null && <span className="ml-1 font-medium text-muted-foreground">({g.delta_4w > 0 ? '+' : ''}{String(g.delta_4w).replace('.', ',')} kg in 4 Wo.)</span>}
          </Zeile>
        )}
        <Zeile icon={CalendarCheck} label="Letzter Check-in">
          {d.letzter_checkin ? <>{tagDe(d.letzter_checkin.faellig)} · {CHECKIN[d.letzter_checkin.stand] || d.letzter_checkin.stand}</> : <span className="font-medium text-muted-foreground">noch keiner</span>}
          {d.urlaub && <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">im Urlaub</span>}
        </Zeile>
        <Zeile icon={Dumbbell} label="Aktueller Plan">
          {d.plan ? d.plan.name : <span className="font-medium text-muted-foreground">kein laufender Trainingsplan</span>}
          {d.ernaehrungsplan && <span className="block font-medium text-muted-foreground">Ernährung {tagDe(d.ernaehrungsplan.start)}–{tagDe(d.ernaehrungsplan.ende)}</span>}
        </Zeile>
        {d.kalorienziel != null && <Zeile icon={Flame} label="Kalorienziel heute">{d.kalorienziel} kcal</Zeile>}
        {d.pt && (
          <Zeile icon={ClipboardList} label="PT-Kontingent">
            {d.pt.wahrgenommen ?? 0} von {d.pt.soll} erbracht · {d.pt.offen} frei
            <span className="block font-medium text-muted-foreground">{d.pt.name}{d.pt.bis ? ` · bis ${tagDe(d.pt.bis)}` : ''}</span>
          </Zeile>
        )}
        {d.woche && <Zeile icon={TrendingUp} label={d.woche.art === 'phase' ? 'Phase' : 'Woche im Programm'}>{d.woche.text}</Zeile>}
      </div>
      {gehe && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button type="button" className={knopf} onClick={() => gehe(d.links.akte)} data-testid="kundeninfo-akte"><UserRound className="mr-1.5 inline h-4 w-4" aria-hidden="true" />Akte</button>
          <button type="button" className={knopf} onClick={() => gehe(d.links.fortschritt)} data-testid="kundeninfo-fortschritt"><TrendingUp className="mr-1.5 inline h-4 w-4" aria-hidden="true" />Fortschritt</button>
          <button type="button" className={knopf} onClick={() => gehe(d.links.ziele)} data-testid="kundeninfo-ziele"><Target className="mr-1.5 inline h-4 w-4" aria-hidden="true" />Ziele</button>
          {d.links.checkins && <button type="button" className={knopf} onClick={() => gehe(d.links.checkins)} data-testid="kundeninfo-checkins"><CalendarCheck className="mr-1.5 inline h-4 w-4" aria-hidden="true" />Check-ins</button>}
          {!ohneChatLink && <button type="button" className={knopf} onClick={() => gehe(d.links.chat)} data-testid="kundeninfo-chat">Chat</button>}
        </div>
      )}
    </section>
  );
}
